import { getChapterOffset, getHomeLabState, HOME_LAB_CHAPTERS } from './homeLabState';

export const WHEEL_GESTURE_GAP_MS = 220;
export const CHAPTER_TRANSITION_MS = 400;
type Position = { offset: number; travel: number };

export function createChapterScroll(view: Pick<Window, 'scrollY' | 'scrollTo' | 'performance' | 'requestAnimationFrame' | 'cancelAnimationFrame'>,
  reducedMotion: boolean) {
  let frame = 0, generation = 0;
  const cancel = () => { generation++; if (frame) view.cancelAnimationFrame(frame); frame = 0; };
  return {
    to(top: number, chapterDistance = Infinity) {
      cancel();
      const from = view.scrollY;
      if (reducedMotion || Math.abs(top - from) <= .75) { view.scrollTo({ top, behavior: 'instant' }); return; }
      const start = view.performance.now(), version = generation;
      const duration = CHAPTER_TRANSITION_MS * Math.max(1, chapterDistance > 0 ? Math.abs(top - from) / chapterDistance : 1);
      const tick = (time: number) => {
        if (version !== generation) return;
        frame = 0;
        const progress = Math.max(0, Math.min(1, (time - start) / duration));
        // Плавность прозрачности задаёт сцена; второй easing сжал бы видимое затухание.
        view.scrollTo({ top: from + (top - from) * progress, behavior: 'instant' });
        if (progress < 1 && version === generation) frame = view.requestAnimationFrame(tick);
      };
      frame = view.requestAnimationFrame(tick);
    },
    cancel,
  };
}

export function createScrollGestureGuard() {
  let gesture: { direction: number; target: number; travel: number; time: number } | null = null;
  const reset = () => { gesture = null; };
  return {
    input({ offset, travel }: Position, delta: number, time: number, continuous = false): number | null {
      const direction = Math.sign(delta);
      if (!Number.isFinite(delta) || !direction || travel <= 0 || offset < 0 || offset > travel
        || (offset <= .75 && direction < 0) || (offset >= travel - .75 && direction > 0)) { reset(); return null; }
      if (!gesture || gesture.travel !== travel || gesture.direction !== direction
        || (!continuous && time - gesture.time >= WHEEL_GESTURE_GAP_MS)) {
        const state = getHomeLabState(offset, travel);
        const chapter = state.fromChapter === state.toChapter ? state.chapter + direction
          : direction > 0 ? state.toChapter : state.fromChapter;
        gesture = { direction, target: getChapterOffset(chapter, travel), travel, time };
      }
      gesture.time = time;
      // Первый импульс сразу запускает переход; инерционный хвост сохраняет тот же якорь.
      return gesture.target;
    },
    moved({ offset, travel }: Position): number | null {
      if (!gesture) return null;
      if (gesture.travel !== travel) { reset(); return null; }
      if (gesture.direction * (offset - gesture.target) <= .75) return null;
      // Последняя/первая категория не запирает нативный выход из сцены.
      if (gesture.target === 0 || gesture.target === travel) { reset(); return null; }
      return gesture.target;
    },
    reset,
  };
}

export function createScrollSettler(read: () => Position, scroll: (offset: number) => void, stop: () => void = () => {}) {
  let previous = read(), direction = 1;
  let disposed = false, held = false, pending = false, autoTarget: number | null = null;
  const partial = ({ offset, travel }: Position) => {
    if (travel <= 0 || offset <= 0 || offset >= travel) return false;
    const state = getHomeLabState(offset, travel);
    return state.fromChapter !== state.toChapter;
  };
  const stopOwnedScroll = () => {
    if (autoTarget === null) return;
    autoTarget = null;
    stop();
    previous = read();
  };
  const finish = (target: number) => {
    if (disposed || autoTarget === target) return;
    stopOwnedScroll();
    pending = false;
    previous = read();
    if (Math.abs(previous.offset - target) <= .75) return;
    autoTarget = target;
    scroll(target);
  };
  const settle = () => {
    if (disposed || held || !pending) return;
    const latest = read();
    pending = false;
    if (latest.travel !== previous.travel || !partial(latest)) { previous = latest; return; }
    const position = latest.offset / latest.travel * (HOME_LAB_CHAPTERS - 1);
    finish(getChapterOffset(direction > 0 ? Math.ceil(position) : Math.floor(position), latest.travel));
  };
  const reset = () => {
    stopOwnedScroll(); pending = false; previous = read();
  };
  return {
    moved() {
      if (disposed) return;
      const latest = read();
      if (latest.travel !== previous.travel) { reset(); return; }
      if (latest.offset === previous.offset) return;
      const delta = latest.offset - previous.offset;
      previous = latest;
      if (autoTarget !== null) {
        if (Math.abs(latest.offset - autoTarget) <= .75 || latest.offset < 0 || latest.offset > latest.travel) autoTarget = null;
        return;
      }
      direction = Math.sign(delta);
      pending = partial(latest);
      settle();
    },
    activity() { if (!disposed) reset(); },
    hold(value: boolean) {
      if (disposed || held === value) return;
      held = value;
      stopOwnedScroll();
      if (held) pending = partial(previous);
      else settle();
    },
    cancel: reset,
    finish,
    dispose() { disposed = true; reset(); },
  };
}

export function attachScrollSettler(root: HTMLElement, read: () => Position,
  scroll: (offset: number) => void, changed: () => void, stop?: () => void) {
  const doc = root.ownerDocument, view = doc.defaultView!;
  const header = doc.querySelector('[data-site-header-row]')?.closest('nav');
  const controller = createScrollSettler(read, scroll, stop ?? (() => view.scrollTo({ top: view.scrollY, behavior: 'instant' })));
  const guard = createScrollGestureGuard();
  const pointers = new Set<number>();
  let touches = 0, touchY: number | null = null, touchTarget: EventTarget | null = null;
  let input: 'wheel' | 'key' | 'touch' | null = null;
  const resetGuard = () => { guard.reset(); input = null; };
  const useInput = (kind: typeof input) => { if (input !== kind) resetGuard(); input = kind; };
  const hold = () => controller.hold(pointers.size > 0 || touches > 0);
  const activity = () => { if (!doc.hidden) controller.activity(); };
  const pageTarget = (target: EventTarget | null, direction: number, keyboard = false) => {
    if (!(target instanceof view.Element)) return target === doc || target === view;
    if (!root.contains(target) && !header?.contains(target) && target !== doc.body && target !== doc.documentElement) return false;
    if (target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="slider"], [role="spinbutton"]')
      || target.closest('dialog, [role="dialog"], [role="menu"], [role="listbox"]')
      || (keyboard && target.closest('a, button, [role="button"]'))) return false;
    for (let node = target; node && node !== root && node !== doc.body; node = node.parentElement) {
      if (node.scrollHeight <= node.clientHeight + 1) continue;
      const style = view.getComputedStyle(node);
      if (/^(auto|scroll|overlay)$/.test(style.overflowY)
        && (/^(contain|none)$/.test(style.overscrollBehaviorY) || (direction < 0 ? node.scrollTop > .75
          : node.scrollTop + node.clientHeight < node.scrollHeight - .75))) return false;
    }
    return true;
  };
  const correctOvershoot = () => {
    const position = read(), target = guard.moved(position);
    if (target !== null) view.scrollTo({ top: view.scrollY - position.offset + target, behavior: 'instant' });
  };
  const onScroll = (event: Event) => {
    if (event.target === doc || event.target === view || event.target === doc.scrollingElement) {
      if (doc.hidden) { controller.cancel(); resetGuard(); }
      else { correctOvershoot(); controller.moved(); }
      changed();
    } else if (event.target instanceof view.Element && root.contains(event.target)) activity();
  };
  const onWheel = (event: WheelEvent) => {
    if (doc.hidden || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey
      || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) { resetGuard(); return; }
    if (!pageTarget(event.target, Math.sign(event.deltaY))) { resetGuard(); activity(); return; }
    useInput('wheel');
    const unit = event.deltaMode === 1 ? parseFloat(view.getComputedStyle(root).lineHeight) || 16 : event.deltaMode === 2 ? view.innerHeight : 1;
    const delta = event.deltaY * unit;
    const target = guard.input(read(), delta, view.performance.now());
    if (target !== null && event.cancelable) { event.preventDefault(); controller.finish(target); }
    else activity();
  };
  const onKey = (event: KeyboardEvent) => {
    if (['Home', 'End', 'Tab'].includes(event.key)) { resetGuard(); activity(); return; }
    if (!event.altKey && !event.ctrlKey && !event.metaKey
      && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', ' '].includes(event.key)) {
      const pageKey = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', ' '].includes(event.key);
      const direction = event.key === 'ArrowUp' || event.key === 'PageUp' || (event.key === ' ' && event.shiftKey) ? -1 : 1;
      if (doc.hidden || event.defaultPrevented || !pageKey || !pageTarget(event.target, direction, true)) { resetGuard(); activity(); return; }
      if (!event.repeat) resetGuard();
      useInput('key');
      const target = guard.input(read(), direction * (event.key.startsWith('Arrow') ? 40 : view.innerHeight), view.performance.now(), true);
      if (target !== null && event.cancelable) { event.preventDefault(); controller.finish(target); }
      else activity();
    }
  };
  const onPointerDown = (event: PointerEvent) => { resetGuard(); pointers.add(event.pointerId); hold(); };
  const onPointerEnd = (event: PointerEvent) => { pointers.delete(event.pointerId); hold(); };
  // При нативном touch-pan приходит pointercancel, но палец всё ещё удерживает жест.
  const onTouch = (event: TouchEvent) => {
    touches = event.touches.length;
    if (event.type === 'touchstart') { resetGuard(); touchTarget = event.target; }
    touchY = touches === 1 ? event.touches[0].clientY : null;
    if (touches > 1) resetGuard();
    hold();
  };
  const onTouchMove = (event: TouchEvent) => {
    if (doc.hidden || touches !== 1 || event.touches.length !== 1 || touchY === null) { resetGuard(); return; }
    const nextY = event.touches[0].clientY;
    if (nextY === touchY) return;
    if (!pageTarget(touchTarget, Math.sign(touchY - nextY))) { resetGuard(); touchY = nextY; return; }
    useInput('touch');
    guard.input(read(), touchY - nextY, view.performance.now(), true);
    touchY = nextY;
  };
  const onClick = () => { resetGuard(); activity(); };
  const reset = () => { controller.cancel(); resetGuard(); pointers.clear(); touches = 0; touchY = null; hold(); };
  const onBlur = (event: FocusEvent) => { if (event.target === view) reset(); };
  const refresh = () => { controller.cancel(); resetGuard(); changed(); };
  const options = { passive: true, capture: true };
  const events = { scroll: onScroll, wheel: onWheel, keydown: onKey, pointerdown: onPointerDown,
    pointerup: onPointerEnd, pointercancel: onPointerEnd, touchstart: onTouch, touchend: onTouch,
    touchcancel: onTouch, touchmove: onTouchMove, click: onClick, resize: refresh, blur: onBlur };
  for (const [type, listener] of Object.entries(events)) view.addEventListener(type, listener as EventListener,
    type === 'wheel' || type === 'keydown' ? { ...options, passive: false } : options);
  doc.addEventListener('visibilitychange', reset, options);
  return { refresh, navigate(target: number) { resetGuard(); controller.finish(target); }, dispose() {
    controller.dispose();
    for (const [type, listener] of Object.entries(events)) view.removeEventListener(type, listener as EventListener, options);
    doc.removeEventListener('visibilitychange', reset, options);
  } };
}
